import { ChangeDetectionStrategy, Component, OnInit, signal, ViewEncapsulation, inject } from '@angular/core';
import {MemberStateService} from '../../services/member-state.service';
import {FormatService} from '../../services/format.service';
import {FileService} from '../../services/file.service';
import {PaymentService} from '../../services/payment.service';
import {UserContextService} from '../../services/user-context.service';
import {AnalyticsService} from '../../services/analytics.service';
import {EMPTY, Observable} from 'rxjs';
import {catchError, concatMap, finalize, map, take, tap} from 'rxjs/operators';
import {format, startOfYear, startOfMonth, endOfYear, subYears, subMonths, isBefore, isAfter, isEqual, parseISO} from 'date-fns';
import {MonthInfo} from '../../model/month-info';
import {LocalizedText} from '../../model/localized-text';
import {ViewState, toViewState} from '../../model/view-state';
import {LettersService} from '../../services/letters.service';
import { IntroductionComponent } from '../shared/introduction/introduction.component';
import { AlertComponent } from '../shared/alert/alert.component';
import { LoaderComponent } from '../shared/loader/loader.component';
import { Bind } from 'primeng/bind';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { Ripple } from 'primeng/ripple';
import { AsyncPipe, DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

interface AttestationsData {
  monthsWithPayments: MonthInfo[];
  regionalPeriods: MonthInfo[];
  lastTrimesterPeriods: MonthInfo[];
  thisYearPeriods: MonthInfo[];
  lastYearPeriods: MonthInfo[];
  LMWP12: MonthInfo | null;
}

@Component({
    selector: 'myabvv-attestations',
    standalone: true,
    templateUrl: 'attestations.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['attestations.css'],
    imports: [
      IntroductionComponent, AlertComponent, LoaderComponent, Bind, Tabs, TabList,
      Ripple, Tab, TabPanels, TabPanel, AsyncPipe, DatePipe, TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class AttestationsComponent implements OnInit {
  private paymentService = inject(PaymentService);
  private memberStateService = inject(MemberStateService);
  private formatService = inject(FormatService);
  private fileService = inject(FileService);
  private lettersService = inject(LettersService);
  private userContext = inject(UserContextService);
  private analyticsService = inject(AnalyticsService);


  state$: Observable<ViewState<AttestationsData>>;
  lastTrimesterPeriod: {begin: string; end: string};
  beginLastTrimester: string;
  endLastTrimester: string;
  beginThisYear        = format(startOfYear(new Date()), 'yyyy-MM');
  endThisYear          = format(new Date(), 'yyyy-MM');
  beginLastYear        = format(subYears(startOfYear(new Date()), 1), 'yyyy-MM');
  endLastYear          = format(endOfYear(subYears(new Date(), 1)), 'yyyy-MM');
  isDownloading = signal(false);
  hasDownloadError = signal(false);
  activeTab = 0;

  private buildAttestationsData(monthInfoList: MonthInfo[]): AttestationsData {
    // Filter out payments we are not interested in
    const monthsWithPayments = monthInfoList.filter(
      (value: MonthInfo) => !(value.mpai === 'B' || value.mpai === 'F' || value.mpai === 'P' || value.mpai === '9')
    );

    // Build regional periods: one entry per distinct docRegion run
    const regionalPeriods: MonthInfo[] = [];
    let prevreg = '';
    let i = -1;
    for (const m of monthsWithPayments) {
      if (m.docRegion !== prevreg) {
        i++;
        regionalPeriods[i] = new MonthInfo();
        prevreg = m.docRegion;
      }
      regionalPeriods[i] = m;
    }

    // Determine LMWP12: latest month with payment if within last 13 months
    let LMWP12: MonthInfo | null = null;
    if (monthsWithPayments.length > 0) {
      const latestMonthWithPayment = monthsWithPayments[0];
      const thirteenMonthsAgo = subMonths(startOfMonth(new Date()), 13);
      if (isBefore(thirteenMonthsAgo, parseISO(latestMonthWithPayment.monthUnemployment))) {
        LMWP12 = latestMonthWithPayment;
      }
    }

    // An amounts-and-days attestation is only offered for a period in which the region has a payment
    const periodsWithPayments = (begin: string, end: string) => regionalPeriods.filter(period =>
      this.hasDataInPeriod(monthsWithPayments.filter(m => m.docRegion === period.docRegion), begin, end));

    return {
      monthsWithPayments,
      regionalPeriods,
      lastTrimesterPeriods: periodsWithPayments(this.beginLastTrimester, this.endLastTrimester),
      thisYearPeriods: periodsWithPayments(this.beginThisYear, this.endThisYear),
      lastYearPeriods: periodsWithPayments(this.beginLastYear, this.endLastYear),
      LMWP12
    };
  }

  ngOnInit() {
    this.lastTrimesterPeriod  = this.formatService.getLastTrimesterPeriod();
    this.beginLastTrimester   = this.lastTrimesterPeriod.begin;
    this.endLastTrimester     = this.lastTrimesterPeriod.end;

    const startMonth = format(subYears(startOfYear(new Date()), 1), 'yyyy-MM');
    const endMonth = format(new Date(), 'yyyy-MM');
    const isInternalUser = this.userContext.isInternalUser();

    // Use /me endpoint for external users, /{inss} for internal users
    const monthsWithPayments$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          concatMap(member => this.paymentService.getMonthsWithPayments(member.personalInformation.inss, startMonth, endMonth))
        )
      : this.paymentService.getMyMonthsWithPayments(startMonth, endMonth);

    this.state$ = monthsWithPayments$.pipe(
      map(monthInfoList => this.buildAttestationsData(monthInfoList)),
      toViewState()
    );
  }

  downloadAttestationOfUnemployment(lid: string, month: string, region: string) {
    this.isDownloading.set(true);
    this.hasDownloadError.set(false);
    const isInternalUser = this.userContext.isInternalUser();

    this.analyticsService.trackEvent('download_attestation', {
      type: 'unemployment',
      month: month
    });

    // Use /me endpoint for external users, /{inss} for internal users
    const download$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          concatMap(member => this.lettersService.getAttestationOfUnemployment(member.personalInformation.inss, lid, month, region))
        )
      : this.lettersService.getMyAttestationOfUnemployment(lid, month, region);

    download$.pipe(
      tap(res => {
        this.fileService.downloadFile(res);
      }),
      catchError(() => {
        this.hasDownloadError.set(true);
        this.analyticsService.trackEvent('download_failed', { type: 'attestation_unemployment' });
        return EMPTY;
      }),
      finalize(() => {
        this.isDownloading.set(false);
      })
    ).subscribe();
  }


  downloadAttestationOfUnemploymentIncome(lid: string, month: string, region: string) {
    this.isDownloading.set(true);
    this.hasDownloadError.set(false);
    const isInternalUser = this.userContext.isInternalUser();

    this.analyticsService.trackEvent('download_attestation', {
      type: 'unemployment_income',
      month: month
    });

    // Use /me endpoint for external users, /{inss} for internal users
    const download$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          concatMap(member => this.lettersService.getAttestationOfUnemploymentIncome(member.personalInformation.inss, lid, month, region))
        )
      : this.lettersService.getMyAttestationOfUnemploymentIncome(lid, month, region);

    download$.pipe(
      tap(res => {
        this.fileService.downloadFile(res);
      }),
      catchError(() => {
        this.hasDownloadError.set(true);
        this.analyticsService.trackEvent('download_failed', { type: 'attestation_unemployment_income' });
        return EMPTY;
      }),
      finalize(() => {
        this.isDownloading.set(false);
      })
    ).subscribe();
  }

  downloadAttestationOfUnemploymentAmountsAndDays(lid: string, begin: string, end: string, region: string) {
    this.isDownloading.set(true);
    this.hasDownloadError.set(false);
    const isInternalUser = this.userContext.isInternalUser();

    this.analyticsService.trackEvent('download_attestation', {
      type: 'unemployment_amounts_days',
      period: `${begin}_${end}`
    });

    // Use /me endpoint for external users, /{inss} for internal users
    const download$ = isInternalUser
      ? this.memberStateService.getMember().pipe(
          take(1),
          concatMap(member =>
            this.lettersService.getAttestationOfUnemploymentAmountsAndDays(member.personalInformation.inss, lid, begin, end, region))
        )
      : this.lettersService.getMyAttestationOfUnemploymentAmountsAndDays(lid, begin, end, region);

    download$.pipe(
      tap(res => {
        this.fileService.downloadFile(res);
      }),
      catchError(() => {
        this.hasDownloadError.set(true);
        this.analyticsService.trackEvent('download_failed', { type: 'attestation_amounts_days' });
        return EMPTY;
      }),
      finalize(() => {
        this.isDownloading.set(false);
      })
    ).subscribe();
  }

  hasDataInPeriod(regionalPeriods: MonthInfo[], begin: string, end: string): boolean {
    if (!regionalPeriods || regionalPeriods.length === 0) {
      return false;
    }
    return regionalPeriods.some(element => {
      const elementDate = parseISO(element.monthUnemployment);
      const beginDate = parseISO(begin);
      const endDate = parseISO(end);
      const isSameOrAfterBegin = isAfter(elementDate, beginDate) || isEqual(elementDate, beginDate);
      const isSameOrBeforeEnd = isBefore(elementDate, endDate) || isEqual(elementDate, endDate);
      return isSameOrAfterBegin && isSameOrBeforeEnd;
    });
  }

  getTranslatedData(array: LocalizedText[]): string {
    return this.formatService.getTranslatedData(array);
  }

  formatMonth(dateString: string): string {
    // Input: "2025-03" -> Output: "03"
    const date = parseISO(dateString);
    return format(date, 'MM');
  }

  formatYear(dateString: string): string {
    // Input: "2025-03" -> Output: "2025"
    const date = parseISO(dateString);
    return format(date, 'yyyy');
  }
}
